import { useApolloClient } from '@apollo/client';
import { Divider, Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    CreatePorschePaymentSettingsDocument,
    type CreatePorschePaymentSettingsMutation,
    type CreatePorschePaymentSettingsMutationVariables,
} from '../../../../api/mutations/createPorschePaymentSettings';
import { Environment, type PorschePaymentSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import PasswordField from '../../../../components/fields/PasswordField';
import SelectField from '../../../../components/fields/SelectField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validator from './validators';

export type AddPorschePaymentSettingsFormProps = {
    moduleId: string;
    onComplete: () => void;
};

type FormValues = PorschePaymentSettingsInput;

const initialValues: FormValues = {
    amount: 0,
    apiKey: '',
    secretKey: '',
    currency: '',
    displayName: '',
    environment: Environment.PreProduction,
    apiEndpoint: '',
    loginEndpoint: '',
    webhookKey: '',
    widgetUrl: '',
    endpointApiVersion: 'v1',
};

export const usePorschePaymentEnvironmentOptions = () => {
    const { t } = useTranslation(['common']);

    return [
        {
            label: t('common:environment.preProduction'),
            value: Environment.PreProduction,
        },
        {
            label: t('common:environment.production'),
            value: Environment.Production,
        },
    ];
};

const AddPorschePaymentSettingsForm = ({ moduleId, onComplete }: AddPorschePaymentSettingsFormProps) => {
    const { t } = useTranslation(['moduleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('porschePaymentModuleDetails:paymentSettingsDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<CreatePorschePaymentSettingsMutation, CreatePorschePaymentSettingsMutationVariables>({
                    mutation: CreatePorschePaymentSettingsDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('porschePaymentModuleDetails:paymentSettingsDrawer.messages.success'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [onComplete, t, apolloClient, moduleId]
    );

    const validate = useValidator(validator());
    const environmentOptions = usePorschePaymentEnvironmentOptions();

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form
                    id="addPorschePaymentSettings"
                    layout="vertical"
                    name="addPorschePaymentSettings"
                    onSubmitCapture={handleSubmit}
                >
                    <Divider>{t('porschePaymentModuleDetails:dividers.mainDetails')}</Divider>
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.currency', {
                            returnObjects: true,
                        })}
                        name="currency"
                    />
                    <InputNumberField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.amount', {
                            returnObjects: true,
                        })}
                        name="amount"
                    />
                    <Divider>{t('porschePaymentModuleDetails:dividers.paymentSettings')}</Divider>
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.apiKey', {
                            returnObjects: true,
                        })}
                        name="apiKey"
                    />
                    <PasswordField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.secretKey', {
                            returnObjects: true,
                        })}
                        name="secretKey"
                    />
                    <SelectField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.environment', {
                            returnObjects: true,
                        })}
                        name="environment"
                        options={environmentOptions}
                        showSearch
                    />
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.apiEndpoint', {
                            returnObjects: true,
                        })}
                        name="apiEndpoint"
                    />
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.loginEndpoint', {
                            returnObjects: true,
                        })}
                        name="loginEndpoint"
                    />
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.widgetUrl', {
                            returnObjects: true,
                        })}
                        name="widgetUrl"
                    />
                    <PasswordField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.webhookKey', {
                            returnObjects: true,
                        })}
                        name="webhookKey"
                    />
                    <InputField
                        {...t('porschePaymentModuleDetails:paymentSettingsDrawer.fields.endpointApiVersion', {
                            returnObjects: true,
                        })}
                        name="endpointApiVersion"
                    />
                </Form>
            )}
        </Formik>
    );
};

export default AddPorschePaymentSettingsForm;
